import { Modal } from 'antd';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { apiClient } from '@/api/client';
import { releaseAllLocksForSignOff } from '@/originator/editorLock';
import { extractApiError } from '@/originator/statusUtils';

import { isDevLoginEnabled } from './devLogin';
import {
  accessTokenFresh,
  accessTokenKnownExpired,
  clearAccessTokenExpiry,
  clearRefreshTokenExpiry,
  clearSessionRefreshStamp,
  markAccessTokenExpiresAt,
  markRefreshTokenExpiresAt,
  markSessionRefreshed,
  readAccessTokenExpiresAt,
  readRefreshTokenExpiresAt,
  REFRESH_COALESCE_MS,
  sessionRefreshedWithin,
  withSessionRefreshLock,
} from './sessionRefreshLock';
import {
  broadcastSessionEnded,
  SessionEndedError,
  SessionRefreshUnavailableError,
  subscribeToSessionActivity,
  subscribeToSessionEnded,
} from './sessionTimer';
import { invalidateSession } from './useSession';

/** Warn this many seconds before the absolute session cap, not on every refresh tick. */
const WARNING_THRESHOLD_SECONDS = 5 * 60;
/** Fallback only — normally overridden by the server-provided refreshIntervalSeconds. */
const DEFAULT_REFRESH_INTERVAL_SECONDS = 15 * 60;
/** How often to check the absolute deadline against the warning threshold. */
const DEADLINE_CHECK_MS = 30_000;

/**
 * Cadence a leader drops to after a forced refresh fails transiently.
 *
 * The forced tick is the only thing that rotates the ISAM refresh token, and that token dies
 * `RefreshTokenTtlMinutes` after the grant that minted it — five minutes after the tick that
 * was supposed to replace it (20 − 15, see AuthOptions). Waiting out another full interval
 * spends that entire margin and arrives to find the credential already gone, so the margin is
 * only usable if something retries inside it.
 */
const REFRESH_RETRY_MS = 60_000;

/**
 * Stop retrying when less than this remains before the refresh token dies. A round trip needs
 * time to land, and an attempt that cannot complete before the deadline only adds a failing
 * request to a session that is already over.
 */
const REFRESH_RETRY_FLOOR_MS = 15_000;

/**
 * The cadence to run at after a forced refresh failed, or `null` to go back to the server's
 * interval because there is no longer anything to race for.
 *
 * <h3>Unknown is not expired</h3>
 * A null `refreshTokenExpiresAt` means the deadline was never reported — a backend predating
 * the field, an Entra session, storage unavailable, or a tab whose first refresh is the one
 * that just failed. It retries anyway, on the flat cadence: the cost of a needless retry is one
 * request, and the cost of not retrying is the session. Only a deadline we actually know to
 * have passed stops the retries.
 *
 * <h3>Why it tightens as the deadline closes</h3>
 * Far out this is a flat minute. Inside two minutes it takes half of what remains, so every
 * attempt leaves room for one more behind it rather than spending the rest of the budget on a
 * single try that may not land.
 */
export function refreshRetryDelayMs(
  refreshTokenExpiresAt: number | null,
  nowMs: number,
): number | null {
  if (refreshTokenExpiresAt === null) return REFRESH_RETRY_MS;

  const remainingMs = refreshTokenExpiresAt * 1000 - nowMs;
  if (remainingMs <= REFRESH_RETRY_FLOOR_MS) return null;

  return Math.min(REFRESH_RETRY_MS, Math.floor(remainingMs / 2));
}

type RefreshResponse = {
  accessTokenExpiresAt: number;
  sessionExpiresAt: number;
  refreshIntervalSeconds: number;
  /**
   * When the ISAM refresh token behind the next grant dies. Null for a session that has no
   * refresh token, absent from a backend predating the field — both mean "unknown", never
   * "expired" (see sessionRefreshLock.ts).
   */
  refreshTokenExpiresAt?: number | null;
};

export type SessionTimerSeed = {
  accessTokenExpiresAt: number;
  sessionExpiresAt: number;
  refreshIntervalSeconds: number;
  /**
   * The refresh-token deadline as of mount, used only to seed the shared stamp when nothing
   * else has (see the effect). The LIVE value lives in sessionRefreshLock's storage, written
   * by every refresh from any tab — read it from there, never from here, and keep it out of
   * the effect's dependency list: /me is cached for the life of the page, so this never
   * updates, and treating it as if it did would restart the interval on a stale number.
   */
  refreshTokenExpiresAt?: number | null;
};

/**
 * Drives the BFF session lifetime for ISAM/Entra sessions:
 *   - Calls POST /auth/refresh on a server-driven interval (default 15 min — see
 *     AuthOptions.RefreshIntervalMinutes) with `force=true`, so the backend's stored ISAM
 *     access token — and the refresh token rotated alongside it — stay current no matter
 *     how quiet the session has been.
 *   - Pings unforced whenever notifySessionActivity() fires (wired into the upload/save/submit
 *     call sites — see sessionTimer.ts), but ONLY when the stored ISAM access token is close
 *     enough to expiry to need one. A save behind a token with minutes of life left sends no
 *     request at all: the BFF would answer it by sliding the cookie and nothing else, and the
 *     save's own request already does that (see BffSessionAuthHandler). That ping sat ahead of
 *     the save on the critical path, so removing it is latency the advisor gets back.
 *   - Re-anchors the interval when a ping turns out to have spent a real grant — detected by
 *     the returned accessTokenExpiresAt moving. Restarting on every ping, as this used to,
 *     pushed the tick out on calls that rotated nothing and widened the window where the
 *     access token sits expired.
 *   - Warns the user (sticky modal, not a dismissible toast — losing unsaved work to a
 *     silent logout would be worse than an extra click) exactly once, five minutes before
 *     the 5-hour absolute session cap. Same modal, same moment, for both schemes: that cap
 *     is measured from sign-in and nothing — not activity, not a refresh, not the button on
 *     the modal — moves it, which is both why it is worth interrupting for and why showing
 *     it a second time would only be the same news on a 30-second loop.
 *   - Redirects to login through the same invalidateSession() → POST /auth/logout →
 *     navigate sequence LogoutPage.tsx already uses, once the session is confirmed gone.
 *
 * No token is ever read from or written to the response — only expiry timestamps, per
 * the BFF's "browser never sees a token" principle (see AuthEndpoints.cs).
 *
 * Pass `null` before the session has resolved; the effect no-ops until a seed arrives.
 * Skips entirely under Dev Login, which has no real backend TTL to refresh against.
 *
 * <h3>Sessions that do not refresh</h3>
 * The first two bullets are ISAM (advisor) only. Entra (originator/approver) sessions have no
 * IdP refresh grant and no sliding cookie, so the server sends `refreshIntervalSeconds: 0` and
 * this hook runs neither the interval nor the activity subscription — see ADR-0033. The last
 * two bullets still apply unchanged: what those sessions lose is the IDLE window, never the
 * 5h cap, so the warning and the redirect are scheme-independent by design.
 *
 * <h3>One refresher across all tabs</h3>
 * Every tab shares one cookie and therefore one session, so N tabs each running this interval
 * meant N unsynchronized grants against one rotating ISAM refresh token — and a collision
 * ends the session for all of them (see sessionRefreshLock.ts). Only the tab elected leader
 * by the slot registry runs the interval; the rest stay subscribed to activity, because a
 * follower about to save still needs a current token, and reach the same grant through the
 * cross-tab lock. `isLeader` defaults to true so a caller that does not participate in the
 * registry behaves exactly as before.
 */
export function useSessionTimer(seed: SessionTimerSeed | null, isLeader = true): void {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const warnedRef = useRef(false);
  const sessionExpiresAtRef = useRef<number | null>(null);
  const modalHandleRef = useRef<{ destroy: () => void } | null>(null);
  /** The refresh currently in flight, if any, and whether it was forced — see `refresh` below. */
  const inFlightRef = useRef<{ promise: Promise<void>; force: boolean } | null>(null);
  /**
   * Restarts the leader's refresh cadence, at the server's interval by default or at an
   * explicit shorter period while retrying a failed tick. Held in a ref because the decision to
   * restart is made inside `runRefresh` — the only place that can see whether the BFF actually
   * spent a grant, or failed to — while the interval itself is owned by the effect below. Null
   * in a follower tab, which runs no interval to restart.
   */
  const restartIntervalRef = useRef<((periodMs?: number) => void) | null>(null);
  /**
   * True while the leader's interval is running the shortened post-failure cadence instead of
   * the server's. Cleared by the next refresh that succeeds, whether or not it spent a grant.
   */
  const retryingRef = useRef(false);

  /**
   * Entra (originator/approver) sessions have nothing to refresh — no IdP grant and no
   * sliding cookie (ADR-0033) — so the server reports `refreshIntervalSeconds: 0` and this
   * tab never polls. Only the IDLE window goes away: the absolute-cap deadline check, its
   * warning modal, and the cross-tab session-ended subscription all still run below.
   *
   * An absent field falls back to the default and therefore to enabled, matching the
   * backend's own fail-safe direction (see MeEndpoint.cs): polling a session that needs no
   * refresh is harmless, while failing to poll an ISAM one strands it mid-application.
   */
  const refreshesEnabled = (seed?.refreshIntervalSeconds ?? DEFAULT_REFRESH_INTERVAL_SECONDS) > 0;

  /**
   * `notifyServer: false` is the path taken by a tab that learned the session was gone from
   * another tab's broadcast. The session has already been deleted server-side, so a second
   * `POST /auth/logout` from each of five tabs would be pure noise.
   */
  const endSession = useCallback(
    (notifyServer: boolean) => {
      modalHandleRef.current?.destroy();
      // Hand back editor locks before the credentials go. Gated on `notifyServer` for the
      // same reason the logout POST below is: the tab that DETECTED the end does the server
      // work, while tabs that learned it from a broadcast would only add redundant calls
      // against a session that is already gone.
      if (notifyServer) void releaseAllLocksForSignOff();
      invalidateSession();
      // Otherwise the next session, in a tab this one never reloaded, would coalesce its
      // first refresh away against a stamp left by the session that just died — or, worse,
      // read the dead session's access-token expiry as licence to skip its first refresh
      // entirely and save on a token that belongs to nobody.
      clearSessionRefreshStamp();
      clearAccessTokenExpiry();
      clearRefreshTokenExpiry();
      if (!notifyServer) {
        navigate('/auth/logged-out', { replace: true });
        return;
      }
      apiClient
        .post('/auth/logout', null, { baseURL: '/' })
        .catch(() => {
          /* best-effort — proceed to the login redirect regardless */
        })
        .finally(() => {
          navigate('/auth/logged-out', { replace: true });
        });
    },
    [navigate],
  );

  const redirectToLogin = useCallback(() => {
    broadcastSessionEnded();
    endSession(true);
  }, [endSession]);

  /**
   * Put the leader's interval onto the post-failure retry cadence — or take it back off, once
   * the refresh token's deadline has passed and there is nothing left to retry for.
   *
   * A follower no-ops here (`restartIntervalRef` is null in one), which is right: its own ping
   * failing says nothing about whether the leader's tick is landing, and a follower that
   * started its own cadence would be a second ticker against one rotating token.
   */
  const scheduleRetry = useCallback(() => {
    const delayMs = refreshRetryDelayMs(readRefreshTokenExpiresAt(), Date.now());
    if (delayMs === null) {
      // The credential these retries were racing is gone. The cookie lapses on the same
      // deadline — SessionCookie caps it there precisely so this lands as a redirect rather
      // than a teardown — so the advisor's next request draws an ordinary 401 and the
      // redirect that already handles it. Hammering until then would only add failures.
      if (retryingRef.current) {
        retryingRef.current = false;
        restartIntervalRef.current?.();
      }
      return;
    }
    retryingRef.current = true;
    restartIntervalRef.current?.(delayMs);
  }, []);

  const runRefresh = useCallback(
    async (force: boolean): Promise<void> => {
      try {
        // Serialized across tabs, with the freshness check INSIDE the lock: whoever queues
        // behind the winner would otherwise wake up and fire a second grant against the
        // refresh token the winner has just rotated away. See sessionRefreshLock.ts.
        //
        // A forced refresh skips the freshness check, because a fresh stamp no longer implies
        // a grant was spent: the BE now answers an unforced call with a no-op whenever the
        // stored access token is still good. Coalescing the tick away against one of those
        // would leave the rotating refresh token untouched until it expired. Only the leader
        // ticks, so this is still at most one forced grant per interval across all tabs.
        await withSessionRefreshLock(async () => {
          if (!force && sessionRefreshedWithin(REFRESH_COALESCE_MS)) return;
          // Read INSIDE the lock: a tab that queued behind another's grant must compare against
          // what that grant left behind, not against the value it saw before waiting.
          const previousExpiry = readAccessTokenExpiresAt();
          const res = await apiClient.post<RefreshResponse>('/auth/refresh', null, {
            baseURL: '/',
            params: force ? { force: true } : undefined,
          });
          markSessionRefreshed();
          sessionExpiresAtRef.current = res.data.sessionExpiresAt;
          markAccessTokenExpiresAt(res.data.accessTokenExpiresAt);
          // Recorded on every refresh, including the ones that spent no grant: an unforced
          // call that renewed nothing still reports the deadline of the token already on the
          // session, which is exactly the value a reader needs when no grant is happening.
          markRefreshTokenExpiresAt(res.data.refreshTokenExpiresAt);

          // A moved expiry is the only evidence a GRANT was actually spent: the BFF answers an
          // unforced call on a still-valid token by sliding the cookie and nothing else, and
          // returns the same timestamp it was given. The cadence used to restart on every ping,
          // which pushed the tick out on calls that rotated nothing and so WIDENED the stretch
          // where the access token sits dead. Anchoring it to real grants keeps the interval
          // measured against the ISAM tokens it exists to rotate.
          // A null `previousExpiry` is NOT evidence of a grant. It means this tab has never
          // recorded one — which is also exactly what a FAILED mount refresh leaves behind, and
          // that is the case that bit: the mount's forced refresh errors, nothing is stamped, and
          // the first save then reads unknown-vs-returned as "the expiry moved". The tick gets
          // re-anchored by a ping that rotated nothing, lands past the refresh token's own
          // deadline, and the session is gone by the time it fires.
          //
          // Unknown therefore declines to re-anchor. The asymmetry is deliberate: refusing can
          // only leave the tick EARLIER than ideal, costing one extra rotation, while a false
          // positive moves it LATER — past the only credential that can renew the session.
          const granted =
            previousExpiry !== null && previousExpiry !== res.data.accessTokenExpiresAt;

          // Whatever was failing has recovered, so the shortened cadence goes back to the
          // server's. Keyed on the call SUCCEEDING rather than on a grant landing: a forced
          // call that legitimately rotates nothing — an ISAM session with no stored refresh
          // token — would otherwise leave this tab polling every minute for the rest of its
          // life, with `granted` false every time.
          const recovered = retryingRef.current;
          retryingRef.current = false;

          if (recovered || granted) restartIntervalRef.current?.();
        });
        // Only when the deadline has genuinely moved back outside the warning window.
        // A refresh CANNOT move the absolute cap — `sessionExpiresAt` is derived as
        // `sessionStartedAt + AbsoluteSessionTtlMinutes` and is fixed for the life of the
        // session (MeEndpoint.cs, AuthEndpoints.HandleRefresh) — so clearing the latch
        // unconditionally re-armed a warning that the deadline check re-fired on its very
        // next tick, so every save/upload ping in the final stretch of a session put the
        // modal back on screen, once per DEADLINE_CHECK_MS.
        const deadline = sessionExpiresAtRef.current;
        const stillInWarningWindow =
          deadline !== null &&
          deadline - Math.floor(Date.now() / 1000) <= WARNING_THRESHOLD_SECONDS;
        if (!stillInWarningWindow) {
          warnedRef.current = false;
          modalHandleRef.current?.destroy();
          modalHandleRef.current = null;
        }
      } catch (err) {
        // A 401 here means the BE already deleted the session (absolute cap exceeded, or
        // the upstream IdP rejected the refresh grant) — the cookie is already cleared.
        // Anything else is a transient error; the next tick or activity ping retries.
        if (extractApiError(err).status === 401) {
          redirectToLogin();
          // Told to whoever awaited the ping, so an action that was about to spend the token
          // abandons instead of firing a request that can only 401 in turn. Deliberately NOT
          // thrown for a transient failure: the stored token is probably still valid, and
          // blocking every save on a blip at the IdP would be the worse failure.
          throw new SessionEndedError();
        }

        // Transient — and for a FORCED tick that is not something to shrug off. This was the
        // call that had to rotate the refresh token, and what remains of that token is the
        // margin between the interval and its own TTL. Dropping to a retry cadence bounded by
        // the deadline the server reports is what makes that margin usable; leaving the full
        // interval in place spends all of it waiting and arrives after the credential is gone.
        //
        // Unforced pings are deliberately left alone: one failing on a token that is still
        // good is a non-event, and the activity subscription below already decides on its own
        // whether the caller may proceed.
        if (force) scheduleRetry();
      }
    },
    [redirectToLogin, scheduleRetry],
  );

  /**
   * One refresh at a time. Concurrent callers join the request already in flight rather than
   * starting a second one — two `POST /auth/refresh` in parallel are two `refresh_token`
   * grants, and ISAM rotates on use, so the loser sends a token the winner has consumed, is
   * refused with `invalid_grant`, and the BFF ends the session.
   *
   * The activity pings are serialized by their callers awaiting them (see sessionTimer.ts);
   * this closes the remaining window, where an interval tick lands on top of one of them.
   *
   * <h3>A forced refresh joins nothing weaker than itself</h3>
   * An unforced refresh may perform no grant at all (the BE skips it while the access token
   * is still valid), so letting the timed tick join one would silently cost that interval its
   * grant — and the grant is the only thing that rotates the refresh token before it expires.
   * It queues behind instead: serialized, which is all the hazard above ever required, rather
   * than dropped. If that earlier refresh rejects, the session is gone and the forced one is
   * abandoned with it.
   */
  const refresh = useCallback(
    (force = false): Promise<void> => {
      const inFlight = inFlightRef.current;
      if (inFlight && (inFlight.force || !force)) return inFlight.promise;

      const run = () => runRefresh(force);
      const promise = (inFlight ? inFlight.promise.then(run) : run()).finally(() => {
        // Only if it is still ours — a refresh that queued behind this one owns the slot now.
        if (inFlightRef.current?.promise === promise) inFlightRef.current = null;
      });
      inFlightRef.current = { promise, force };
      return promise;
    },
    [runRefresh],
  );

  /**
   * One notice, {@link WARNING_THRESHOLD_SECONDS} before the absolute cap, identical for both
   * schemes: sign-in + 5h is the only deadline either of them has that the user can neither
   * see nor extend, so it is the only one worth interrupting them for.
   *
   * Purely an acknowledgement — it starts no refresh. ISAM sessions already refresh on their
   * own cadence, Entra sessions have nothing to refresh (ADR-0033), and neither kind of
   * refresh moves this deadline. A button that spent a grant here would imply it bought time
   * it cannot buy.
   */
  const showExpiryWarning = useCallback(() => {
    if (warnedRef.current) return;
    warnedRef.current = true;
    modalHandleRef.current = Modal.warning({
      title: t('session.warningTitle'),
      content: t('session.warningBody'),
      okText: t('session.warningOk'),
      closable: false,
      keyboard: false,
      maskClosable: false,
    });
  }, [t]);

  useEffect(() => {
    if (isDevLoginEnabled() || !seed) return;

    sessionExpiresAtRef.current = seed.sessionExpiresAt;

    // Seed the refresh-token deadline from /me so this tab knows it BEFORE its first refresh
    // lands — which is precisely the case that needs it, since a failed mount refresh is the
    // one failure with no earlier response to have recorded a deadline from.
    //
    // Only when storage holds nothing: the live value belongs to whichever tab refreshed most
    // recently, and /me here is a mount-time snapshot that useSession caches for the life of
    // the page. Overwriting with it would walk a current deadline backwards.
    if (readRefreshTokenExpiresAt() === null) {
      markRefreshTokenExpiresAt(seed.refreshTokenExpiresAt);
    }

    let cancelled = false;
    let refreshIntervalId: number | undefined;

    const restartRefreshInterval = (periodMs: number) => {
      window.clearInterval(refreshIntervalId);
      refreshIntervalId = window.setInterval(() => {
        // Forced: this tick is the one thing guaranteed to spend a grant and rotate the ISAM
        // refresh token before it expires. Everything else only refreshes a token that has
        // actually aged out.
        //
        // Nothing awaits the interval tick, so its rejection is swallowed here — the 401 case
        // has already run the redirect inside `refresh`.
        if (!cancelled) void refresh(true).catch(() => undefined);
      }, periodMs);
    };

    // `?? `, not `|| `: the server sends 0 to mean "this session does not refresh", and a
    // falsy-test would read that as missing and restore the 15-minute poll it was asking us
    // to stop. The fallback is for an ABSENT field only.
    const intervalSeconds = seed.refreshIntervalSeconds ?? DEFAULT_REFRESH_INTERVAL_SECONDS;
    const periodMs = intervalSeconds * 1000;

    // Only the leader owns an interval, so only the leader has one to re-anchor when a grant
    // lands. A follower's grant leaves the leader's cadence alone: the leader's next tick is
    // then merely early, which costs one extra rotation and nothing else.
    restartIntervalRef.current =
      refreshesEnabled && isLeader
        ? (overrideMs?: number) => restartRefreshInterval(overrideMs ?? periodMs)
        : null;

    if (refreshesEnabled && isLeader) {
      // A fresh interval is the server's cadence, not a retry left over from a previous
      // leadership term.
      retryingRef.current = false;
      restartRefreshInterval(periodMs);
      // Take a turn immediately on assuming leadership, rather than waiting out a first full
      // period. A handoff happens precisely because the previous leader went quiet, so its
      // interval is already gone; a silent period on top of the one it was frozen for could
      // outlast the bmo_session cookie's 20-minute Max-Age — and, worse, the refresh token's
      // own TTL, which nothing rotated while the old leader was frozen. Forced for that
      // reason: the new leader's first turn is the timed cadence restarting, and it is the
      // turn most likely to find the refresh token close to the edge. Still one grant across
      // five tabs opening together, because only one of them is the leader.
      void refresh(true).catch(() => undefined);
    }

    // Upload/save/submit activity resets the cadence and refreshes right away, so an
    // active advisor never hits the interval boundary mid-action. Followers subscribe too:
    // they don't tick, but a follower about to save still needs the token behind that save to
    // be current, and the cross-tab lock makes asking for it safe from any tab.
    //
    // The refresh is RETURNED, not discarded: notifySessionActivity() hands it back to the
    // caller, so an awaiting call site holds its request until the token behind it is current,
    // and learns — as a rejection — when the session turned out to be gone.
    //
    // A non-refreshing session subscribes nothing at all. The save/upload call sites await
    // ensureSessionRefreshed() unconditionally, and with no listener registered that resolves
    // immediately off Promise.all([]) — so they proceed unblocked rather than waiting on a
    // request that would do nothing. See sessionTimer.ts.
    const unsubscribe = refreshesEnabled
      ? subscribeToSessionActivity(async () => {
          if (cancelled) return;

          // The token still has real life in it, so there is nothing for the BFF to do and no
          // reason to make the caller wait on a round-trip to be told so. The session itself is
          // kept alive by the request the caller is about to send — BffSessionAuthHandler
          // slides the cookie on any first-party request — and by the timed tick, so skipping
          // here costs the session nothing.
          if (accessTokenFresh()) return;

          await refresh();

          // The token was already spent when we got here, so this refresh was not optional. If
          // it is STILL expired the grant did not happen — a transient failure, or a BFF that
          // has no refresh token to spend — and the caller must not send a request on a token
          // we know is dead. Checked on the outcome rather than on a flag threaded through, so
          // it holds just as well when this ping joined a refresh another tab had in flight.
          //
          // Deliberately the narrow "known expired" test, not the wide freshness margin above:
          // a ping at ninety seconds to spare is answered with no grant by design, and treating
          // that as a failure would block a save the token could have carried.
          if (accessTokenKnownExpired()) throw new SessionRefreshUnavailableError();
        })
      : () => {};

    // One tab's 401 means every tab's session is gone — they share the cookie. Without this
    // the followers, which no longer tick, could sit on a dead session indefinitely.
    const unsubscribeSessionEnded = subscribeToSessionEnded(() => {
      if (!cancelled) endSession(false);
    });

    // Deliberately decoupled from the refresh interval above: a recent activity ping
    // restarts that timer, but the absolute deadline below is fixed and must still be
    // checked on its own cadence regardless of how recently the user was active.
    const deadlineCheckId = window.setInterval(() => {
      if (cancelled || sessionExpiresAtRef.current === null) return;
      const secondsLeft = sessionExpiresAtRef.current - Math.floor(Date.now() / 1000);
      if (secondsLeft <= 0) {
        redirectToLogin();
      } else if (secondsLeft <= WARNING_THRESHOLD_SECONDS) {
        showExpiryWarning();
      }
    }, DEADLINE_CHECK_MS);

    return () => {
      cancelled = true;
      restartIntervalRef.current = null;
      window.clearInterval(refreshIntervalId);
      window.clearInterval(deadlineCheckId);
      unsubscribe();
      unsubscribeSessionEnded();
    };
    // Intentionally re-runs only when the seed's own values change, not on every
    // refresh/showExpiryWarning identity change (those are stable-enough callbacks that
    // close over refs) — mirrors the eslint-disable already used for this kind of
    // effect in useEditorLock.ts / LogoutPage.tsx.
    //
    // `isLeader` IS in the list: both of the values above are fixed for the life of a session
    // (the absolute cap and a server constant), so leadership changing is the only thing that
    // re-runs this effect — which is exactly when the interval has to start or stop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed?.sessionExpiresAt, seed?.refreshIntervalSeconds, isLeader]);
}
